import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/apiClient';
import { getDefaultCompanyId } from 'utils/helpers';

export enum QR_STATUS {
    ALL = 'ALL',
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    DELETED = 'DELETED',
    PENDING = 'PENDING',
}

export interface MerchantQRCode {
    companyCode: string;
    createdDate: string;
    description: string;
    entityId: number;
    id: string;
    label: string;
    status: QR_STATUS;
    updatedDate: string;
    uuid: string;
}
export interface MerchantQRString {
    qrId: string;
    string: string;
}

export interface AddQRPayload {
    companyId: number;
    description: string;
    id: number;
    label: string;
    status: QR_STATUS;
    valid: boolean;
    validUpdate: boolean;
}

interface CreateQRCodePayload {
    label: string;
    description: string;
}

interface QRCodeState {
    allQrCodes: MerchantQRCode[];
    qrStrings: MerchantQRString[];
    loading: boolean;
    error: boolean;
}
const initialState: QRCodeState = {
    allQrCodes: [],
    qrStrings: [],
    loading: false,
    error: false,
};

export const getQRCodes = createAsyncThunk('qrCode/getQRCodes', async (status: QR_STATUS, { getState }) => {
    const state: any = getState();
    const companyId = getDefaultCompanyId(state);
    if (!companyId) return;

    // FIX ME

    const res: any = await client.get('/merchant/qrCode', {
        headers: {
            companyId,
            status,
        },
    });
    const companyQRCodes = res?.companyQRCodes;
    const prevStrings = state.qrCode?.qrStrings || [];
    const qrStrings = await generateQRStrings(companyQRCodes, prevStrings);
    return { companyQRCodes, qrStrings };
});

const generateQRStrings = async (qrCodes: MerchantQRCode[], prevStrings: MerchantQRString[]) => {
    if (!Array.isArray(qrCodes) || !qrCodes?.length) return [];
    const qrStrings: MerchantQRString[] = [];
    let qrString = '';
    await Promise.all(
        qrCodes.map(async (qr) => {
            const qrStringObject = prevStrings?.find((qrString) => qrString.qrId === qr.id);
            if (!qrStringObject) {
                qrString = await encodeQRString(JSON.stringify({ ...qr, isMerchant: true }));
            } else {
                qrString = qrStringObject.string;
            }
            qrStrings.push({ qrId: qr.id, string: qrString });
        })
    );
    return qrStrings;
};

const encodeQRString = async (str: string): Promise<string> => {
    const res: any = await client.post('merchant/encodeString', { stringRequest: str });
    return res;
};

// export const downloadQR = (id: string, filename: string) => {
//     const canvas = document.getElementById(id) as HTMLCanvasElement;
//     console.log('$$$$$ ~ downloadQR ~ canvas:', canvas)
//     const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
//     const downloadLink = document.createElement('a');
//     downloadLink.href = pngUrl;
//     downloadLink.download = `${filename}.png`;
//     document.body.appendChild(downloadLink);
//     downloadLink.click();
//     document.body.removeChild(downloadLink);
// };
export const downloadQR = (id: string, filename: string) => {
    const svg = document.getElementById(id) as SVGElement | null;
    if (!svg) return;

    // Serialize the SVG element to a string
    const svgData = new XMLSerializer().serializeToString(svg);

    // Create a Blob with the SVG data
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);

    // Create a download link
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = `${filename}.svg`;

    // Append the link, click it, and remove it
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    // Revoke the object URL
    URL.revokeObjectURL(url);
};

export const getQRCodeById = (strings: MerchantQRString[], id: string) => {
    if (!Array.isArray(strings) || !id) return '';
    return strings.find((str) => str.qrId === id)?.string || '';
};

export const createQRCode = createAsyncThunk('qrCode/createQRCode', async ({ label, description }: CreateQRCodePayload, { getState }) => {
    const state: any = getState();
    const companyId = getDefaultCompanyId(state);
    const businessName = state?.user?.userResponse?.accountInfo?.businessName;
    const nickName = state?.user?.userResponse?.accountInfo?.nickName;

    if (!companyId) return;

    const payload = {
        companyId,
        label,
        description,
        id: 0,
        status: QR_STATUS.ACTIVE,
        valid: true,
        validUpdate: true,
        businessName,
        nickName,
    };

    const res: any = await client.post('merchant/qrCode', payload);
    return res;
});

const qrCodeSlice = createSlice({
    name: 'qrCode',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getQRCodes.pending, (state) => {
            state.loading = true;
            state.error = false;
        });
        builder.addCase(getQRCodes.fulfilled, (state, action) => {
            state.loading = false;
            state.error = false;
            const { payload } = action || {};
            if (!payload) return;
            state.allQrCodes = payload.companyQRCodes;
            state.qrStrings = payload.qrStrings;
        });
        builder.addCase(getQRCodes.rejected, (state) => {
            state.loading = false;
            state.error = true;
        });
        builder.addCase(createQRCode.pending, (state) => {
            state.loading = true;
            state.error = false;
        });
        builder.addCase(createQRCode.fulfilled, (state) => {
            state.loading = false;
            // NO NEED TO UPDATE STATE AS WE WILL EVENTUALLY FETCH THE GENERATED QR CODE WITH GETQRCODE API
            // ADDED THE BUILDER CASE TO SHOW THE LOADING UNTIL QR CODES ARE RETURNED FROM BACKEND
        });
        builder.addCase(createQRCode.rejected, (state) => {
            state.loading = false;
            state.error = true;
        });
    },
});

export default qrCodeSlice.reducer;
